import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getOrcamentoById,
  getDefaults,
  updateOrcamentoItem,
  concluirConferenca,
  atualizarRequisicoes,
  reabrirOrcamento,
  reconhecerItem,
  reverificarOrcamento,
  getHistoricoProdutoNasajon,
  getHistoricoOrcamento,
  getUnidadesNasajon,
} from '../../../api/orcamentoCiliaApi';
import { OrcamentoItem, HistoricoVersao } from '../../../interfaces/OrcamentoCilia';
import { getEsteiras } from '../../../api/esteirasApi';
import { queryKeys } from '../../../infrastructure/react-query/queryKeys';
import { useIsTi } from '../../../hooks/useUserType';
import { useAlcanca } from '../../../hooks/usePapeis';
import CotacaoOnlineSecao from '../components/CotacaoOnlineSecao';
import AvisoPerdaTotal from '../components/AvisoPerdaTotal';
import MotivoDaRequisicao from '../components/MotivoDaRequisicao';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ImportXmlModal from '../components/ImportXmlModal';
import AvisoCotacaoAberta from '../components/AvisoCotacaoAberta';
import ItemDetalheModal from '../components/ItemDetalheModal';
import EscolhaDoDestino, { EscolhaParcial } from '../components/EscolhaDoDestino';
import { ORCAMENTOS_CILIA, ORCAMENTOS_CILIA_DETAIL } from '../routes';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import '../../recepcao/pages/Recepcao.css';
import '../../../components/como-funciona/help-circle-btn.css';
// A paleta de badges era carregada só pelo CSS da LISTA (esta tela usava as classes sem
// importar nada — bundle único do CRA escondia). Import explícito desde a extração.
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/aviso-de-tela.css';
import './OrcamentoCiliaDetail.css';

const ITEMS_PER_PAGE = 10;

const OrcamentoCiliaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTi = useIsTi();
  // Leitura: o TI (ADR 0012) e, desde a at-15, quem não é orçamentista — COMPRA abre a tela para
  // decidir a compra pela cotação online, e a conferência continua sendo do orçamentista.
  const podeConferir = useAlcanca(['ORCAMENTO']);
  const somenteLeitura = isTi || !podeConferir;
  // Compras abre direto na cotação online, que é o que interessa a ele; os demais, na conferência.
  const [aba, setAba] = useState<'CONFERENCIA' | 'COTACAO'>(() => (!isTi && !podeConferir ? 'COTACAO' : 'CONFERENCIA'));

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isSituacoesHelpOpen, setIsSituacoesHelpOpen] = useState(false);
  // "Subir complemento" (29/07/2026): import restrito a complementos DESTE
  // orçamento/veículo. O "+ Importar XML" da listagem segue irrestrito.
  const [isComplementoModalOpen, setIsComplementoModalOpen] = useState(false);
  // "Histórico do Orçamento": painel lateral com todas as versões da cadeia
  const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
  const [isConcludeConfirmOpen, setIsConcludeConfirmOpen] = useState(false);
  // at-14: oficina e veículo, reiniciados pela pré-seleção do servidor a cada abertura do modal.
  const [escolhaDestino, setEscolhaDestino] = useState<EscolhaParcial>({ tipoOficina: null, tipoVeiculo: null });
  // Os estados dos dois modais de desfecho MANUAL saíram na at-11 junto com eles: o desfecho é
  // lido do ERP, e não há mais nada a digitar aqui.

  // Estado local de digitação do código do produto Nasajon (por item)

  // Página atual de cada grupo de situação (paginação client-side por grupo)
  const [groupPages, setGroupPages] = useState<Record<string, number>>({});

  // Histórico do PMC (modal ao clicar no valor)
  // Peça aberta no detalhe — só o ID (ver `itemDetalhe`, derivado do dado fresco).
  const [itemDetalheId, setItemDetalheId] = useState<string | null>(null);
  const [historicoTarget, setHistoricoTarget] = useState<OrcamentoItem | null>(null);
  const [historicoPage, setHistoricoPage] = useState(1);
  const HISTORICO_POR_PAGINA = 8;


  // Reconhecer peça (busca de produto no ERP)

  const budgetId = id || '';

  // Query orçamento
  const { data: orcamento, isLoading, isError } = useQuery({
    queryKey: queryKeys.orcamentoCilia.detail(budgetId),
    queryFn: () => getOrcamentoById(budgetId),
    enabled: !!budgetId,
  });

  /*
   * Tempo real (ADR 0003 do módulo): a decisão do Chefe sobre um complemento chega enquanto
   * a conferência está aberta — antes só aparecia recarregando a página.
   *
   * ⚠️ **Filtra pelo id da tela**: um evento de OUTRO orçamento não pode fazer esta
   * conferência recarregar (a tela tem estado de edição em andamento; recarga desnecessária
   * é ruído em cima de quem está trabalhando).
   */
  useEffect(() => {
    if (!budgetId) return;

    const aoAtualizar = (evento: Event) => {
      const detail = (evento as CustomEvent<{ orcamentoId?: string }>).detail;
      if (detail?.orcamentoId !== budgetId) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
    };

    window.addEventListener('cilia:orcamento-atualizado', aoAtualizar);
    return () => window.removeEventListener('cilia:orcamento-atualizado', aoAtualizar);
  }, [budgetId, queryClient]);

  // Histórico da cadeia (modal): só busca com o modal aberto
  const { data: historicoOrc, isLoading: buscandoHistoricoOrc } = useQuery({
    queryKey: queryKeys.orcamentoCilia.historico(budgetId),
    queryFn: () => getHistoricoOrcamento(budgetId),
    enabled: !!budgetId && isHistoricoOpen,
  });

  // Query esteiras
  const { data: esteiras } = useQuery({
    queryKey: ['esteiras'],
    queryFn: () => getEsteiras(false),
  });

  /*
   * Unidades do cadastro do ERP (at-11) — o seletor do modal da peça.
   *
   * ⚠️ `staleTime` alto de propósito: é cadastro que quase não muda (146 unidades medidas em
   * 18/09/2026) e o backend já mantém cache próprio de 10 minutos. Refazer a consulta a cada
   * abertura de modal custaria uma ida ao ERP por clique.
   */
  const unidadesQuery = useQuery({
    queryKey: queryKeys.orcamentoCilia.unidades,
    queryFn: getUnidadesNasajon,
    staleTime: 10 * 60 * 1000,
  });

  // Margem do alerta de PMC (configurável na T4) — PMC é custo histórico e
  // "atrasa"; só alerta acima de PMC + margem%
  const { data: defaults } = useQuery({
    queryKey: queryKeys.orcamentoCilia.defaults,
    queryFn: getDefaults,
  });
  const margemPmcPct = (() => {
    const n = parseFloat(defaults?.PMC_ALERTA_MARGEM_PCT ?? '50');
    return Number.isFinite(n) && n >= 0 ? n : 50;
  })();
  const mesesJanela = (() => {
    const n = parseInt(defaults?.MEDIA_COMPRAS_MESES ?? '6', 10);
    return Number.isFinite(n) && n > 0 ? n : 6;
  })();

  // Referência de preço = média das compras da janela. O PMC foi descartado
  // como referência (decisão do Victor, 28/07/2026): sem compras no período o
  // item simplesmente fica sem referência.
  const referenciaPreco = (item: OrcamentoItem): number | null => {
    const media = item.mediaCompras ? parseFloat(item.mediaCompras) : 0;
    return media > 0 ? media : null;
  };

  const filteredEsteiras = esteiras?.filter((est) => !est.sistema && est.ativo) || [];

  // Mutations
  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: any }) =>
      updateOrcamentoItem(budgetId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao salvar alteração');
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
    }
  });

  const reverificarMutation = useMutation({
    mutationFn: () => reverificarOrcamento(budgetId),
    onSuccess: (r) => {
      toast.success(`Reverificação concluída: ${r.existentes} reconhecida(s) no ERP, ${r.aCriar} a criar.`);
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao reverificar produtos no Nasajon');
    },
  });

  /**
   * Peças que vão virar PRODUTO NOVO no Nasajon ao concluir: a comprar nesta versão (herdada já foi
   * pedida antes) e sem vínculo com produto do ERP. É o mesmo recorte que o backend cria no envio.
   */
  const pecasSemVinculo = useMemo(
    () =>
      (orcamento?.itens ?? []).filter(
        (i) => i.situacaoCompra === 'A_COMPRAR' && i.origemDiff !== 'HERDADO' && i.produtoStatus === 'A_CRIAR',
      ),
    [orcamento],
  );

  const concluirMutation = useMutation({
    mutationFn: () =>
      concluirConferenca(budgetId, {
        // O botão só habilita com as duas escolhas feitas; o `as` reflete essa guarda.
        tipoOficina: escolhaDestino.tipoOficina as 'INTERNA' | 'EXTERNA',
        tipoVeiculo: escolhaDestino.tipoVeiculo as 'CARRO' | 'MOTO',
      }),
    onSuccess: (data) => {
      setIsConcludeConfirmOpen(false);
      toast.success('Conferência concluída com sucesso!');
      const { criados, existentes, simulados, requisicoesCriadas, semRequisicao, codigosRepetidos, destino } =
        data.resultado;
      if (destino && (requisicoesCriadas ?? 0) > 0) {
        toast.info(
          `Requisição criada com centro de custo ${destino.centroCusto}, classificação ${destino.classificacaoFinanceira} e local de estoque ${destino.localEstoque}.`,
          { autoClose: 12000 },
        );
      }
      /*
       * 🔴 NÚMERO REPETIDO (at-14): o número segue a sequência do Nasajon, e Compras criou outra
       * requisição com ele no mesmo segundo. Nada se confunde no módulo (o acompanhamento é pelo
       * id), mas Compras precisa saber que há dois documentos com o mesmo número.
       */
      if (codigosRepetidos && codigosRepetidos.length > 0) {
        toast.warn(
          `O Nasajon tem outra requisição com o número ${codigosRepetidos.join(', ')}. Avise Compras: são dois documentos com o mesmo número.`,
          { autoClose: false },
        );
      }
      toast.info(`Peças: ${existentes} existentes, ${criados} criadas, ${simulados} simuladas.`, {
        autoClose: 8000,
      });
      /*
       * DUAS REQUISIÇÕES É O CASO DA VERSÃO MISTA (at-11): quem concluiu precisa saber que existem
       * dois documentos a acompanhar no ERP — as peças de CAMINHO VIP vão numa requisição separada,
       * com o marcador que avisa o comprador para não comprar. Sem este aviso, a pessoa acompanha
       * uma e dá a outra por perdida.
       */
      if ((requisicoesCriadas ?? 0) > 1) {
        toast.info(
          `Foram criadas ${requisicoesCriadas} requisições no Nasajon: uma com as peças a comprar e outra com as peças já compradas na rua (CAMINHO VIP).`,
          { autoClose: 12000 },
        );
      }
      /*
       * 12/08/2026 (BUG-002): a conclusão pode terminar SEM requisição — e sem este aviso o
       * usuário fica procurando um card de requisição que nunca vai aparecer. Desde o ADR
       * 0006 (20/08/2026) há um motivo só: a versão não trouxe peça comprável.
       */
      if (semRequisicao === 'NENHUM_ITEM_COMPRAVEL') {
        toast.warn(
          'Nenhuma requisição foi criada: esta versão não trouxe peça nova a comprar (as que aparecem como requisitadas foram pedidas em versões anteriores). Não há nada a cotar por esta versão.',
          { autoClose: 12000 },
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.all });
    },
    onError: (err: any) => {
      setIsConcludeConfirmOpen(false);
      if (err.code === 'ITENS_PENDENTES' && orcamento?.itens) {
        toast.error('Existem itens com pendências de triagem ou NCM.');
        // Rolar para o primeiro item com pendência
        const firstPending = orcamento.itens.find(
          (item) =>
            item.situacaoCompra === 'A_COMPRAR' &&
            (!item.esteiraIdSoac || item.produtoStatus === 'NAO_VERIFICADO')
        );
        if (firstPending) {
          // O item pode estar em outra página do grupo — troca a página antes de rolar
          const grupo = firstPending.situacaoCompra;
          const itensDoGrupo = [...orcamento.itens]
            .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || a.id.localeCompare(b.id))
            .filter((i) => i.situacaoCompra === grupo);
          const idx = itensDoGrupo.findIndex((i) => i.id === firstPending.id);
          if (idx >= 0) {
            setGroupPages((prev) => ({ ...prev, [grupo]: Math.floor(idx / ITEMS_PER_PAGE) + 1 }));
          }
          setTimeout(() => {
            const element = document.getElementById(`item-${firstPending.id}`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element?.classList.add('highlight-row-pending');
            setTimeout(() => {
              element?.classList.remove('highlight-row-pending');
            }, 3000);
          }, 150);
        }
      } else {
        toast.error(err.message || 'Erro ao concluir conferência');
      }
    }
  });

  const reabrirMutation = useMutation({
    mutationFn: () => reabrirOrcamento(budgetId),
    onSuccess: () => {
      toast.success('Orçamento reaberto para ajustes.');
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.all });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao reabrir orçamento');
    }
  });

  // Histórico da peça no ERP (modal do PMC) — busca ao vivo no Nasajon
  const {
    data: historico,
    isFetching: buscandoHistorico,
    error: erroHistorico,
  } = useQuery({
    queryKey: ['orcamento-cilia', 'historico-produto', historicoTarget?.codigoNasajon ?? historicoTarget?.codigoCilia],
    queryFn: () => getHistoricoProdutoNasajon(historicoTarget?.codigoNasajon || historicoTarget?.codigoCilia || ''),
    enabled: !!historicoTarget,
    retry: false,
  });

  // A busca ao vivo no cadastro do Nasajon vive em `components/BuscaProdutoNasajon.tsx`,
  // dentro do detalhe da peça (13/10/2026) — era um modal separado aberto por uma lupa.
  const reconhecerMutation = useMutation({
    mutationFn: ({
      itemId,
      produtoNasajonId,
      codigoNasajon,
    }: {
      itemId: string;
      produtoNasajonId: string | null;
      codigoNasajon?: string | null;
    }) => reconhecerItem(budgetId, itemId, produtoNasajonId, codigoNasajon),
    onSuccess: (_data, vars) => {
      toast.success(vars.produtoNasajonId ? 'Peça reconhecida no Nasajon!' : 'Vínculo desfeito.');
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao reconhecer peça');
    },
  });

  // O envio de PEDIDO ao ERP foi removido (29/07/2026): o módulo produz a REQUISIÇÃO de compra,
  // que nasce no "Concluir e Enviar" — pedido é etapa posterior da governança de Compras.
  // A mutation de "Forçar compra" saiu em 20/08/2026 (ADR 0006), junto com a regra de
  // estoque de que ela era o escape.

  /*
   * ATUALIZAR A SITUAÇÃO DA REQUISIÇÃO (at-11) — substituiu as duas ações manuais de desfecho.
   *
   * Antes alguém olhava a tela do Nasajon e transcrevia aqui ("Registrar Aprovada" / "Registrar
   * Rejeitada"). Como a requisição é criada por API e tem código no ERP, o desfecho passou a ser
   * **lido**: este botão é o mesmo caminho do job de 10 em 10 minutos, escopado a este orçamento,
   * para quem está com a tela aberta não precisar esperar o ciclo.
   */
  const atualizarRequisicoesMutation = useMutation({
    mutationFn: () => atualizarRequisicoes(budgetId),
    onSuccess: (r) => {
      /*
       * ⚠️ "Nada mudou" e "atualizei" são desfechos DIFERENTES, e os dois são normais. Um toast de
       * sucesso genérico faria o usuário achar que a situação mudou quando a requisição continua
       * exatamente onde estava — e clicar de novo, e de novo.
       */
      if (r.atualizadas > 0) {
        toast.success(
          r.atualizadas === 1 ? 'A situação da requisição foi atualizada.' : `${r.atualizadas} requisições foram atualizadas.`,
        );
      } else {
        toast.info('Consultado no Nasajon: a situação continua a mesma.');
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.detail(budgetId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.all });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Não foi possível consultar a situação no Nasajon.');
    },
  });

  if (isLoading) {
    return (
      <div className="cilia-detail-loading">
        <div className="spinner"></div>
        <span>Carregando conferência...</span>
      </div>
    );
  }

  if (isError || !orcamento) {
    return (
      <div className="cilia-detail-error">
        <h3>Erro ao carregar orçamento</h3>
        <button className="cilia-btn secondary" onClick={() => navigate(ORCAMENTOS_CILIA())}>
          Voltar para Lista
        </button>
      </div>
    );
  }

  const isEditable = (orcamento.estado === 'EM_CONFERENCIA' || orcamento.estado === 'EM_AJUSTE') && !somenteLeitura;

  // Versão superada (29/07/2026): já existe complemento mais novo (não-negado)
  // — esta versão vira leitura (o backend também bloqueia concluir/reabrir/
  // enviar-pedido com VERSAO_SUPERADA). O CSV e o desfecho seguem liberados.
  const versoesAtivas = (orcamento.cadeia ?? []).filter((c) => c.estado !== 'AUTORIZACAO_NEGADA');
  const ultimaVersaoAtiva = versoesAtivas[versoesAtivas.length - 1];
  const isVersaoSuperada =
    !!ultimaVersaoAtiva &&
    ultimaVersaoAtiva.id !== orcamento.id &&
    orcamento.estado !== 'AUTORIZACAO_NEGADA' &&
    ultimaVersaoAtiva.versaoCilia > orcamento.versaoCilia;
  const rotuloUltimaVersao = ultimaVersaoAtiva
    ? ultimaVersaoAtiva.tipo === 'INICIAL'
      ? `Inicial (v${ultimaVersaoAtiva.versaoCilia})`
      : `${ordinalComplemento(ultimaVersaoAtiva.versaoCilia) ?? ''}º Complemento (v${ultimaVersaoAtiva.versaoCilia})`
    : '';

  /*
   * Detalhe da peça (13/08/2026): guarda o **id**, não o objeto.
   *
   * As ações do modal (triagem, vínculo com o ERP) disparam mutations que
   * reescrevem o orçamento; com um snapshot do clique, o modal seguiria mostrando o estado
   * anterior à ação que o próprio usuário acabou de fazer. Derivando do `orcamento` a cada
   * render, o que está na tela é sempre o dado corrente — e o modal se fecha sozinho se o
   * item deixar de existir.
   */
  const itemDetalhe = itemDetalheId
    ? (orcamento.itens ?? []).find((i) => i.id === itemDetalheId) ?? null
    : null;
  const situacaoDoItemDetalhe = itemDetalhe?.situacaoCompra ?? '';

  // Ordenação estável no cliente (ordem do XML + id como desempate): editar um
  // item nunca pode movimentá-lo na tela, independente da ordem da resposta.
  const itensOrdenados = [...(orcamento.itens ?? [])].sort(
    (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || a.id.localeCompare(b.id),
  );

  // Contagem de pendências — espelha exatamente a trava do backend: só o que
  // VAI SER COMPRADO (A_COMPRAR) precisa de triagem e verificação no ERP.
  // Itens aguardando cobertura ou fora do fluxo não bloqueiam.
  const noEsteiraCount = orcamento.itens?.filter(
    (item) => item.situacaoCompra === 'A_COMPRAR' && !item.esteiraIdSoac
  ).length || 0;

  const naoVerificadoCount = orcamento.itens?.filter(
    (item) => item.situacaoCompra === 'A_COMPRAR' && item.produtoStatus === 'NAO_VERIFICADO'
  ).length || 0;

  const totalPendencies = noEsteiraCount + naoVerificadoCount;

  // Agrupamento dos itens pela situação de compra, em ordem de importância:
  // o que foi efetivamente pedido vem primeiro; contexto (fora do fluxo) fica
  // no fim. Grupos vazios não são renderizados — na conferência, "A Comprar"
  // naturalmente assume o topo. O grupo "Em Estoque" saiu em 20/08/2026 (ADR 0006).
  const situacoesOrdem = [
    'ENVIADO',
    'A_COMPRAR',
    'AGUARDANDO_COBERTURA',
    'RECEBIDO',
    'FORA_DO_FLUXO'
  ];

  const situacaoLabels: Record<string, string> = {
    A_COMPRAR: 'A Comprar',
    // Nome de exibição "Sob Análise" (decisão do Victor, 29/07/2026 — termo do
    // Cilia/empresa; "cobertura" não é nomenclatura usada na casa). O valor
    // interno AGUARDANDO_COBERTURA permanece no enum/banco.
    AGUARDANDO_COBERTURA: 'Sob Análise',
    ENVIADO: 'Requisitada', // peça incluída na requisição (pedido saiu do escopo, 29/07)
    RECEBIDO: 'Recebido',
    FORA_DO_FLUXO: 'Fora do Fluxo'
  };

  // Explicação de cada situação — o "?" do grupo abre o painel lateral
  // (mesmo componente ComoFunciona do "Como Funciona" da tela).
  const situacaoAjudaSections: ComoFuncionaSecao[] = [
    {
      titulo: 'A Comprar',
      conteudo: (
        <div>
          <p>
            Peças com <strong>troca autorizada no Cilia</strong> (Impacto/Autorizado). Toda peça deste grupo é
            comprada, na quantidade que o orçamento pede.
          </p>
          <p>São estas que entram na requisição de compra quando você clica em <strong>Concluir e enviar</strong>.</p>
          <p>
            <strong>O estoque do Nasajon não interfere aqui.</strong> O setor de Compras não se guia por aquele
            saldo, então nenhuma peça é retirada da compra, nem tem a quantidade reduzida, por causa dele.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Sob Análise',
      conteudo: (
        <div>
          <p>
            Peças que precisam ser trocadas, mas que estão marcadas como <strong>"Sob Análise" no Cilia</strong> —
            a cobertura delas ainda não foi decidida.
          </p>
          <p>
            <strong>A análise não acontece aqui.</strong> Ela é feita no Cilia, por quem decide a cobertura do
            evento. Nada nesta tela, e nada no sistema da oficina, conclui essa análise ou tira a peça deste
            grupo — não há o que você possa fazer por ela por aqui.
          </p>
          <p>
            <strong>O que fazer: seguir normalmente.</strong> Estas peças não entram na requisição de compra e
            <strong> não impedem</strong> o "Concluir e enviar" — dar esteira e vincular produto nelas é opcional.
            Você conclui o orçamento com as demais.
          </p>
          <p>
            <strong>Como a espera termina:</strong> quando o Cilia liberar a cobertura, a peça volta num
            complemento já como Impacto/Autorizado. Ao importar esse complemento, ela é reanalisada com os
            dados novos (aparece como <strong>Reprocessada</strong>) e passa para "A Comprar", entrando na
            requisição daquela versão. Enquanto nenhum complemento vier, ela permanece aqui.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Solicitada',
      conteudo: (
        <div>
          <p>Peças já incluídas em uma <strong>requisição de compra</strong> (criada no Nasajon ao "Concluir e Enviar").</p>
          <p>
            ⚠️ Em complementos, este grupo mistura duas coisas: as peças que <strong>esta versão</strong> pediu e as
            que <strong>versões anteriores</strong> já haviam pedido (marcadas com <strong>"Pedida na vN · não
            repete aqui"</strong>). A requisição desta versão leva <strong>somente as primeiras</strong> — e são só
            elas que seguem para cotação por aqui. O texto abaixo do título do grupo diz quantas são de cada tipo.
          </p>
          <p>
            Quando a versão não trouxe nenhuma peça nova a comprar, nenhuma requisição é criada — nesse
            caso o grupo mostra apenas peças herdadas, e a cotação daquela versão não terá o que cotar.
          </p>
          <p>
            ⚠️ <strong>Esta lista é a foto do orçamento DESTA versão, não o histórico de tudo o que já foi pedido
            para a placa.</strong> Por isso a contagem pode <strong>diminuir</strong> de um complemento para o
            outro: se o Cilia tirou do orçamento uma peça que já havia sido solicitada, ela deixa de fazer parte
            do orçamento e sai da tabela — o <strong>pedido dela continua existindo</strong>. Essas peças aparecem
            em <strong>"Itens removidos neste complemento"</strong>, no topo da tela, com aviso de atenção: é
            compra que o orçamento não cobre mais, e a decisão (cancelar, aproveitar em outro veículo) é sua.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Recebido',
      conteudo: (
        <div>
          <p>Peças cuja <strong>entrada já foi confirmada no Nasajon</strong> — a peça comprada chegou.</p>
        </div>
      ),
    },
    {
      titulo: 'Fora do Fluxo',
      conteudo: (
        <div>
          <p>Itens do orçamento que <strong>não geram compra de peça</strong>: serviços (mão de obra) e reparos executados pela própria oficina (troca = não no Cilia).</p>
          <p>Exibidos para manter o retrato completo do orçamento do Cilia.</p>
        </div>
      ),
    },
  ];

  // Ordinal do complemento na cadeia real (versões do Cilia podem pular
  // números — ex.: v1 → v4 é o 1º complemento). Negados ficam fora da conta.
  // Function declaration (hoisted): usada também antes deste ponto do arquivo.
  function ordinalComplemento(versaoCilia: number): number | null {
    const complementos = (orcamento?.cadeia ?? [])
      .filter((c) => c.tipo === 'COMPLEMENTAR' && c.estado !== 'AUTORIZACAO_NEGADA')
      .map((c) => c.versaoCilia)
      .sort((a, b) => a - b);
    const posicao = complementos.indexOf(versaoCilia);
    return posicao >= 0 ? posicao + 1 : null;
  }

  // Coluna "Origem": de qual arquivo/versão a peça veio.
  const rotuloOrigem = (item: OrcamentoItem): string => {
    // Legado (importado antes da coluna existir): assume a versão do próprio orçamento.
    const versaoOrigem = item.versaoOrigem ?? orcamento?.versaoCilia ?? 1;
    // at-90: a base da cadeia pode ser uma versão > 1 (o inicial nunca foi importado). Quem a
    // define é o tipo INICIAL da cadeia, não o número da versão.
    const versaoDaBase =
      (orcamento?.cadeia ?? []).find((c) => c.tipo === 'INICIAL')?.versaoCilia ??
      (orcamento?.tipo === 'INICIAL' ? orcamento.versaoCilia : 1);
    if (versaoOrigem <= versaoDaBase) return 'Inicial';
    const ordinal = ordinalComplemento(versaoOrigem);
    return ordinal ? `${ordinal}º Complemento` : `Complemento (v${versaoOrigem})`;
  };

  // "Conferência Concluída" no lugar de "Enviado" (decisão do Victor, 29/07):
  // o estado marca a conferência fechada + requisição criada — o pedido ao fornecedor
  // é outro passo, e "Enviado" dava a entender que já foi.
  const estadoLabelHistorico: Record<string, string> = {
    EM_CONFERENCIA: 'Em Conferência',
    ENVIADO: 'Requisição Criada',
    AGUARDANDO_APROVACAO: 'Aguardando Aprovação',
    APROVADO: 'Requisição Aprovada',
    REJEITADO: 'Requisição Reprovada',
    EM_AJUSTE: 'Em Ajuste',
    AGUARDANDO_AUTORIZACAO: 'Aguardando Autorização',
    AUTORIZACAO_NEGADA: 'Autorização Negada',
  };

  /*
   * Rótulo do estado de UMA versão da cadeia (13/08/2026).
   *
   * `ENVIADO` cobre dois desfechos: a conferência fechou criando requisição, ou fechou sem
   * criar (versão sem peça comprável — `concluirSemRequisicao` no backend). O mapa sozinho
   * diria "Requisição Criada" nos dois casos, contradizendo o toast da conclusão e mandando
   * o usuário procurar no Portal um card que não existe.
   */
  const rotuloEstadoVersao = (versao: { estado: string; requisicoes?: unknown[] }) => {
    if (versao.estado === 'ENVIADO' && (versao.requisicoes?.length ?? 0) === 0) {
      return 'Sem Requisição';
    }
    return estadoLabelHistorico[versao.estado] ?? versao.estado;
  };

  const dataHoraHistorico = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

  /*
   * Situação da REQUISIÇÃO (não do orçamento!), no vocabulário de quem lê a tela.
   *
   * ⚠️ `CRIADA` não é "aguardando aprovação": é a requisição que existe no ERP e que ninguém
   * moveu ainda — e a diferença importa, porque `EM_APROVACAO` significa que ela **entrou** na
   * fila de um gestor. `CANCELADA` tem rótulo próprio de propósito (decisão do Victor,
   * 18/09/2026): para o orçamento vale como reprovação, mas quem lê não pode concluir que um
   * gestor recusou o pedido — a conversa com Compras é outra.
   */
  const rotuloDesfecho = (status: string) =>
    ({
      CRIADA: 'Criada — ainda não avaliada',
      EM_APROVACAO: 'Em aprovação',
      APROVADA: 'Aprovada',
      REPROVADA: 'Reprovada',
      CANCELADA: 'Cancelada no Nasajon',
    })[status] ?? status;

  // Papel da peça dentro de uma versão do histórico (badge por linha).
  const papelDaPeca = (
    item: HistoricoVersao['itens'][number],
    versao: HistoricoVersao,
  ): { rotulo: string; classe: string } | null => {
    if (versao.tipo === 'INICIAL') return null;
    if (item.origemDiff === 'HERDADO') {
      return {
        rotulo: `Já solicitada${item.versaoOrigem ? ` (desde a v${item.versaoOrigem})` : ''}`,
        classe: 'badge-cinza',
      };
    }
    if (item.origemDiff === 'QTD_AUMENTADA') {
      const diferenca = Math.max(
        parseFloat(item.quantidade) - parseFloat(item.quantidadeJaSolicitada ?? '0'),
        0,
      );
      return { rotulo: `Qtd. aumentada (+${diferenca})`, classe: 'badge-laranja' };
    }
    if (item.versaoOrigem && item.versaoOrigem < versao.versaoCilia) {
      return { rotulo: 'Reprocessada', classe: 'badge-azul' };
    }
    return { rotulo: 'Nova nesta versão', classe: 'badge-verde' };
  };

  // Datas do Nasajon vêm como "YYYY-MM-DD" — new Date() as trata como UTC e o
  // fuso do Brasil joga para o dia anterior. Formatar sem converter.
  const formatDateOnly = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (val: string | number | null | undefined): string => {
    if (val === null || val === undefined) return '—';
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '—';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const handleEsteiraChange = (itemId: string, val: string) => {
    if (!val) {
      updateItemMutation.mutate({ itemId, data: { esteiraIdSoac: null, esteiraNome: null } });
      return;
    }
    const selected = filteredEsteiras.find(e => e.id === val);
    if (selected) {
      updateItemMutation.mutate({
        itemId,
        data: { esteiraIdSoac: selected.id, esteiraNome: selected.nome }
      });
    }
  };

  // Seções de ajuda para "Como Funciona"
  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'Processo de Triagem',
      conteudo: (
        <div>
          <p>
            Para cada peça classificada para compra, selecione a <strong>Esteira SOAC</strong> apropriada e
            confira o vínculo com o <strong>Produto no Nasajon</strong>: digite o código ou a descrição da peça e
            escolha, na lista que vem do cadastro do ERP, o produto correspondente. Sem vínculo, a peça é
            cadastrada no ERP como nova no envio; <strong>desfazer vínculo</strong> a devolve a essa condição.
          </p>
          <p>Itens destacados em amarelo na tabela são pendências que precisam ser sanadas antes de prosseguir.</p>
          <p>
            O sistema <strong>aprende</strong> com a conferência: esteira e vínculo de produto informados uma
            vez chegam pré-preenchidos nas próximas importações da mesma peça.
          </p>
        </div>
      )
    },
    {
      titulo: 'Nasajon fora do ar',
      conteudo: (
        <div>
          <p>
            O reconhecimento das peças consulta o cadastro do Nasajon <strong>em tempo real</strong>. Se o ERP
            estiver indisponível na importação, o orçamento entra mesmo assim — os itens ficam como{' '}
            <strong>"Não Verificado"</strong> e contam como pendência.
          </p>
          <p>
            Quando o Nasajon voltar, use o botão <strong>"Reverificar no Nasajon"</strong> (rodapé da
            conferência) para refazer o reconhecimento em lote. O "Concluir e Enviar" também exige o ERP no ar
            — criar peça às cegas poderia gerar duplicata.
          </p>
        </div>
      )
    },
    {
      titulo: 'Referência de preço (média de compras)',
      conteudo: (
        <div>
          <p>
            O XML do Cilia traz o preço cotado, mas <strong>nenhum valor de referência</strong>. A referência vem
            do Nasajon: é a <strong>média das compras da peça nos últimos {mesesJanela} meses</strong> —{' '}
            <em>ponderada pela quantidade</em> (uma compra de 10 unidades pesa mais que uma de 1) e considerando
            só entradas por nota de compra. É o que a Associação realmente pagou no período. A janela é
            configurável em <strong>Administração do Sistema</strong> &rarr; <strong>Requisições</strong>.
          </p>
          <p>
            Quando o valor unitário do item fica <strong>acima da média + a margem configurada</strong>{' '}
            (Administração do Sistema &rarr; Requisições &rarr; "Alerta de preço", padrão 50%), a referência aparece{' '}
            <strong style={{ color: '#dc2626' }}>em vermelho</strong> — sinal para questionar o preço antes de
            comprar. A margem existe porque a média é histórica e "atrasa" em relação ao preço de hoje; sem ela,
            o alerta dispararia quase sempre.
          </p>
          <p>
            <strong>Clique no valor</strong> para abrir o detalhe no ERP: valor do orçamento × média, última
            compra e o <strong>extrato de movimentações</strong> (data, operação, quantidade, valor pago e
            documento). As linhas marcadas como compra são entradas por nota; as demais são ajustes de
            inventário.
          </p>
          <p>
            <strong>Quando aparece "—":</strong> a peça não foi reconhecida no cadastro do Nasajon (sem vínculo,
            não há o que consultar) ou <strong>não teve nenhuma compra nos últimos {mesesJanela} meses</strong> —
            fica sem referência e sem alerta. Nesses casos, o extrato completo (clicando no traço não abre) pode
            ser visto depois que a peça for vinculada.
          </p>
        </div>
      )
    },
    {
      titulo: 'Status ERP',
      conteudo: (
        <div>
          <p>
            Indica a situação da <strong>peça no cadastro de produtos do ERP Nasajon</strong> (não confundir
            com a situação da compra). Na importação, o sistema consulta o cadastro do Nasajon{' '}
            <strong>em tempo real</strong> pelos códigos das peças:
          </p>
          <ul>
            <li><strong>Existente:</strong> a peça já existe no ERP — não será criada duplicata; no envio, a descrição do produto é atualizada conforme o Cilia.</li>
            <li><strong>A Criar:</strong> não foi reconhecida — será cadastrada no ERP no "Concluir e Enviar" (defaults + NCM padrão).</li>
            <li><strong>Criado:</strong> a peça foi efetivamente cadastrada no ERP pelo envio.</li>
            <li><strong>Não Verificado:</strong> item fora do fluxo de compra, ou o Nasajon estava indisponível na importação (use "Reverificar").</li>
          </ul>
          <p><strong>Como mudar o vínculo:</strong> pelo campo "Produto Nasajon" (código, validado no ERP), pela
            lupa (busca por descrição) ou pelo "desfazer" (volta a peça nova). Todo vínculo é memorizado para
            as próximas importações.</p>
        </div>
      )
    },
    {
      titulo: 'Fora do Fluxo',
      conteudo: (
        <div>
          <p>
            São os itens do orçamento que <strong>não geram compra de peça</strong>. Eles aparecem na tela
            apenas para manter o retrato completo do orçamento do Cilia — não pedem esteira nem NCM e não
            contam como pendência.
          </p>
          <p>Um item cai em "Fora do Fluxo" em dois casos, definidos pelo próprio orçamento do Cilia:</p>
          <ul>
            <li>
              <strong>É um serviço (mão de obra):</strong> pintura, alinhamento, desmontagem etc. — não há
              peça a comprar.
            </li>
            <li>
              <strong>É peça marcada como reparo, não troca:</strong> o orçamentista do Cilia decidiu que a
              peça será recuperada pela oficina (só horas de mão de obra), então não há compra.
            </li>
          </ul>
          <p>
            Regra geral da triagem: só vira compra <strong>peça com troca</strong>. Dessas, a marcação do
            Cilia decide o grupo — Impacto/Autorizado vai para "A Comprar"; item "Sob Análise" fica no grupo
            Sob Análise até a análise concluir. O <strong>estoque do Nasajon não participa dessa decisão</strong>:
            peça com troca autorizada é comprada, na quantidade que o orçamento pede.
          </p>
        </div>
      )
    },
    {
      titulo: 'Cotação online',
      conteudo: (
        <div>
          <p>
            Ao clicar em "Concluir e Enviar", o sistema pesquisa sozinho o preço de cada peça a comprar em cinco canais
            (concessionárias, Mercado Livre, ferro-velho, paralelas e lojas de autopeças). O resultado aparece na aba{' '}
            <strong>Cotação online</strong>, no topo da tela, com o menor preço, o link do anúncio e a data da pesquisa, e pode ser
            baixado em planilha. Peças de CAMINHO VIP não são pesquisadas: já foram compradas.
          </p>
          <p>
            Quem decide a compra é <strong>Compras</strong>, que abre esta tela em leitura, já na aba Cotação online. A aba
            tem a própria ajuda, no{' '}
            <strong>?</strong> ao lado do título.
          </p>
        </div>
      )
    },
    {
      titulo: 'Requisição de Compra',
      conteudo: (
        <div>
          <p>
            A requisição é o <strong>produto final deste app</strong>: ela é criada <strong>no Nasajon</strong>{' '}
            quando você clica em "Concluir e Enviar", com as peças aprovadas na conferência, e sai{' '}
            <strong>em seu nome</strong> — por isso o seu usuário precisa estar vinculado a um usuário do
            ERP (a Administração do Sistema faz esse vínculo). Pedido de compra <strong>não</strong> é
            feito por aqui: é a etapa seguinte da governança de compras.
          </p>
          <p>
            O card no fim da tela mostra o <strong>código da requisição no Nasajon</strong> — é por ele
            que o comprador acha o documento lá. O <strong>desfecho</strong> (aprovada ou reprovada){' '}
            <strong>não é digitado aqui</strong>: o sistema consulta o ERP sozinho, de tempos em tempos, e
            o botão "Atualizar situação" pergunta na hora, para quem não quer esperar.
          </p>
          <p>
            Quando a requisição é <strong>reprovada</strong> (ou cancelada no Nasajon), as peças voltam
            para "A comprar" e o botão "Ajustar e Reenviar" reabre o orçamento — o reenvio gera uma{' '}
            <strong>nova versão</strong>, mantendo o histórico.
          </p>
          <p>
            <strong>Atenção a duas coisas que confundem:</strong> requisição aprovada continua com a
            situação de compra "pendente" no ERP (ela só muda quando Compras vira o pedido); e uma
            versão com peças marcadas como <strong>já compradas na rua</strong> gera{' '}
            <strong>duas requisições</strong> — uma normal e outra com o aviso de CAMINHO VIP.
          </p>
        </div>
      )
    },
    {
      titulo: 'Motivo da Requisição',
      conteudo: (
        <div>
          <p>
            O <strong>Motivo da Requisição</strong> é o texto que o comprador lê no campo de mesmo nome do
            Nasajon: placa, modelo, oficina e endereço de entrega, chassi, evento e o nº do Cilia. Ele é{' '}
            <strong>montado pelo sistema</strong>, não digitado, e aparece no topo desta tela.
          </p>
          <p>
            Enquanto a requisição está em conferência, o topo mostra o motivo <strong>que será enviado</strong>,
            com os dados de agora. Depois do envio, mostra o texto <strong>exatamente como foi enviado</strong>:
            se algo mudar no orçamento depois, o que está no Nasajon não muda.
          </p>
          <p>
            Requisições enviadas antes de o sistema passar a guardar o motivo aparecem como{' '}
            <strong>não registrado</strong>. O texto delas continua no Nasajon.
          </p>
        </div>
      )
    },
    {
      titulo: 'Número, centro de custo e local de estoque da requisição',
      conteudo: (
        <div>
          <p>
            A requisição recebe o <strong>próximo número da sequência do Nasajon</strong> — a mesma
            que a tela de Compras usa. Antes de criar, o sistema confere que o número está livre.
          </p>
          <p>
            No "Concluir e Enviar" você informa <strong>onde o veículo está</strong> (oficina interna ou
            parceira) e o <strong>tipo de veículo</strong> (carro ou moto). É isso que define, no Nasajon,
            o <strong>centro de custo</strong>, a <strong>classificação financeira</strong> e o{' '}
            <strong>local de estoque</strong>. O sistema já deixa marcado o que o arquivo do Cilia indica,
            quando indica — confira antes de concluir.
          </p>
          <p>
            Se a combinação escolhida ainda não estiver configurada (ou o que está configurado não existir
            mais no Nasajon), a conclusão é recusada <strong>antes</strong> de qualquer coisa ser criada lá,
            e a mensagem diz o que falta. Quem configura é o ADMIN ou o TI, em Administração do Sistema →
            Requisições.
          </p>
          <p>
            Raramente, Compras pode criar uma requisição com o mesmo número no mesmo instante. A linha da
            requisição mostra <strong>Número repetido no Nasajon</strong>: avise Compras. O acompanhamento
            da aprovação não se confunde.
          </p>
        </div>
      )
    },
    {
      titulo: 'Peça já comprada na rua (CAMINHO VIP)',
      conteudo: (
        <div>
          <p>
            Às vezes a peça <strong>já foi comprada</strong> antes de a requisição existir — e aí o
            comprador não pode comprá-la de novo. Marque a peça no detalhe dela (abrindo pela descrição)
            em <strong>"Já comprada na rua (CAMINHO VIP)"</strong>.
          </p>
          <p>
            As peças marcadas vão para uma <strong>requisição separada</strong>, cujo Motivo da
            Requisição começa com o aviso <strong>CAMINHO VIP</strong> — é assim que Compras sabe que
            aquela lista é só registro, e não ordem de compra. As demais peças seguem na requisição
            normal, na mesma conclusão.
          </p>
        </div>
      )
    },
    {
      titulo: 'Unidade de medida da peça',
      conteudo: (
        <div>
          <p>
            Toda peça vai para a requisição com uma <strong>unidade</strong> (UN, KIT, M2, JOGO...). Para
            peça <strong>nova</strong>, você escolhe a unidade no detalhe dela — a lista vem do cadastro
            do Nasajon, e só valem as unidades que existem lá.
          </p>
          <p>
            Para peça que <strong>já existe</strong> no ERP, a unidade é a do cadastro dela e não pode ser
            alterada aqui: a requisição não pode contradizer o produto que ela mesma referencia.
          </p>
        </div>
      )
    },
    {
      titulo: 'Peça que sai do orçamento e já está sendo comprada',
      conteudo: (
        <div>
          <p>
            Quando um complemento <strong>remove</strong> uma peça, ou <strong>reduz</strong> a
            quantidade dela, essa mudança não aparece sozinha para quem está comprando: a requisição
            criada leva apenas o que foi <strong>acrescentado</strong> em cada complemento. Por isso a
            conferência confere aqui, ao abrir: se a peça que saiu estiver numa cotação em andamento,
            aparece um aviso no alto do bloco de conferência, com o número da cotação e o nome das peças.
          </p>
          <p>
            Nesse caso o sistema <strong>avisa quem está cotando</strong> — a pessoa recebe o aviso no
            Portal de Cotações no mesmo momento, sem depender de ninguém lembrar de comunicar. O próprio
            aviso na tela diz se isso deu certo: quando não dá, ele pede que você faça o contato. Sendo
            urgente, vale confirmar direto com a pessoa de qualquer forma — depois que a cesta é fechada,
            o pedido sai com a peça que o orçamento não pede mais.
          </p>
        </div>
      )
    }
  ];

  /*
   * As requisições DESTA versão (at-11). São até duas: a normal e a de CAMINHO VIP.
   *
   * ⚠️ Ordenadas com a normal primeiro — a VIP é a exceção, e abrir por ela faria o leitor supor
   * que as peças da versão inteira já foram compradas na rua.
   */
  const requisicoes = [...(orcamento.requisicoes ?? [])].sort((a, b) => Number(a.vip) - Number(b.vip));

  /*
   * Há o que consultar no ERP? Requisição já resolvida não muda mais, e a simulada não existe lá —
   * perguntar por ela traria "não encontrada" a cada clique. Sem isso, o botão "Atualizar situação"
   * viveria na tela sem nunca mudar nada, que é a melhor forma de ensinar alguém a ignorá-lo.
   */
  const temRequisicaoAberta = requisicoes.some(
    (r) => !r.simulada && !!r.nasajonCodigo && (r.status === 'CRIADA' || r.status === 'EM_APROVACAO'),
  );

  /** O motivo da reprovação, para o banner de orçamento rejeitado. */
  const motivoDaReprovacao = requisicoes.find((r) => r.motivoRejeicao)?.motivoRejeicao ?? null;

  return (
    <div className="ConteDo recepcao-conte-do" style={{ paddingBottom: '100px', gap: '20px' }}>
      {/* Cabeçalho */}
      <div className="recepcao-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingTop: '40px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div 
            className="CtaBig MouseHover" 
            onClick={() => navigate(ORCAMENTOS_CILIA())} 
            style={{ marginRight: 0, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <div className="Frame1000003987">
              <div className="ComponentName" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                &larr; Voltar
              </div>
            </div>
          </div>
          
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {orcamento.placa}
            <span className={`cilia-badge ${
              orcamento.estado === 'EM_CONFERENCIA' ? 'badge-azul' :
              orcamento.estado === 'ENVIADO' ? 'badge-cinza' :
              orcamento.estado === 'APROVADO' ? 'badge-verde' :
              orcamento.estado === 'REJEITADO' ? 'badge-vermelho' :
              orcamento.estado === 'AUTORIZACAO_NEGADA' ? 'badge-vermelho' :
              orcamento.estado === 'AGUARDANDO_AUTORIZACAO' ? 'badge-laranja' :
              orcamento.estado === 'EM_AJUSTE' ? 'badge-laranja' : 'badge-cinza'
            }`}>
              {
                orcamento.estado === 'EM_CONFERENCIA' ? 'Em Conferência' :
                /*
                  `ENVIADO` tem dois desfechos (13/08/2026): com requisição criada e sem
                  (versão sem peça comprável). Dizer "Requisição Criada" no segundo caso
                  contradiz o toast da conclusão e manda o usuário procurar no Portal um card
                  que não existe.
                */
                orcamento.estado === 'ENVIADO'
                  ? (requisicoes.length > 0 ? 'Requisição Criada' : 'Sem Requisição') :
                orcamento.estado === 'AGUARDANDO_APROVACAO' ? 'Aguardando Aprovação' :
                orcamento.estado === 'APROVADO' ? 'Requisição Aprovada' :
                orcamento.estado === 'REJEITADO' ? 'Requisição Reprovada' :
                orcamento.estado === 'AGUARDANDO_AUTORIZACAO' ? 'Aguardando Autorização' :
                orcamento.estado === 'AUTORIZACAO_NEGADA' ? 'Autorização Negada' :
                orcamento.estado === 'EM_AJUSTE' ? 'Em Ajuste' : orcamento.estado
              }
            </span>
            {orcamento.teste && (
              <span
                className="cilia-badge badge-laranja"
                title='Requisição de teste, marcada na importação: o Motivo no Nasajon começa por "TESTE - NÃO COMPRAR".'
              >
                Teste
              </span>
            )}
            {orcamento.tipo === 'COMPLEMENTAR' && (
              <span
                className="cilia-badge badge-azul"
                title={`Orçamento complementar (versão ${orcamento.versaoCilia} do Cilia) — foto cumulativa: inclui os itens das versões anteriores`}
              >
                {ordinalComplemento(orcamento.versaoCilia)
                  ? `${ordinalComplemento(orcamento.versaoCilia)}º Complemento`
                  : `Complemento v${orcamento.versaoCilia}`}
              </span>
            )}
            {/* at-12: iniciada sem o veículo na Oficina. O motivo vai no title: o selo diz o
                estado, e quem quer saber por quê passa o mouse. */}
            {!orcamento.carId && (
              <span
                className="cilia-badge badge-cinza"
                title={
                  orcamento.semVinculoMotivo === 'EVENTO_DIVERGENTE'
                    ? 'Iniciada sem vínculo: a placa estava na Oficina com outro evento, e quem importou confirmou que não era aquele atendimento. Será ligada sozinha se o veículo entrar com o evento deste orçamento.'
                    : 'Iniciada sem o veículo na Oficina. É ligada sozinha quando ele der entrada na Recepção e alguém abrir a tela de Requisições.'
                }
              >
                Sem atendimento na Oficina
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            className="CtaBig MouseHover"
            onClick={() => setIsHistoricoOpen(true)}
            title="Todas as versões deste orçamento: quem importou, quando, o que mudou em cada complemento, requisições e autorizações"
            style={{ marginRight: 0 }}
          >
            <div className="Frame1000003987">
              <div className="ComponentName">Histórico do Orçamento</div>
            </div>
          </div>
          {!somenteLeitura && (
            <div
              className="CtaSmall MouseHover"
              onClick={() => setIsComplementoModalOpen(true)}
              title={`Importar um orçamento complementar do ${orcamento.placa} (só complementos deste veículo)`}
              style={{ marginRight: 0 }}
            >
              <div className="ComponentName">+ Subir complemento</div>
            </div>
          )}
          <button
            className="help-circle-btn"
            onClick={() => setIsHelpOpen(true)}
            title="Como Funciona"
          >
            ?
          </button>
        </div>
      </div>

      {/* Card de informações gerais */}
      <div className="cilia-detail-header-card" style={{ width: '100%', boxSizing: 'border-box' }}>
        <div className="header-main-info">
          <div className="header-grid">
            <div className="grid-item">
              <span className="label">Veículo</span>
              <span className="value">{orcamento.dadosVeiculo?.nomeVeiculo || '—'}</span>
            </div>
            <div className="grid-item">
              <span className="label">Nº Cilia / Versão</span>
              <span className="value">{orcamento.numeroCilia} (v{orcamento.versaoCilia})</span>
            </div>
            <div className="grid-item">
              <span className="label">Evento</span>
              <span className="value">{orcamento.numeroEvento}</span>
            </div>
            <div className="grid-item">
              <span className="label">Evento (Cilia)</span>
              <span className="value">{orcamento.numeroSinistro || '—'}</span>
            </div>
            <div className="grid-item">
              <span className="label">Oficina</span>
              <span className="value">{orcamento.dadosOficina?.nome || '—'}</span>
            </div>
            <div className="grid-item">
              <span className="label">Importado em</span>
              <span className="value">{new Date(orcamento.createdAt).toLocaleString('pt-BR')}</span>
            </div>
          </div>
          {/* at-18: o Motivo da Requisição do Nasajon, visível ao entrar. */}
          <MotivoDaRequisicao orcamento={orcamento} requisicoes={requisicoes} />
        </div>
      </div>

      {/*
        Abas (at-15, pedido do Victor em 20/10/2026: "muita tabela numa tela só"): a conferência e a
        cotação online são trabalhos diferentes, de pessoas diferentes — o orçamentista confere,
        Compras decide a compra pelo preço online.
      */}
      <AvisoPerdaTotal orcamentoId={budgetId} />

      <div className="recepcao-tabs">
        <button
          type="button"
          className={`recepcao-tab ${aba === 'CONFERENCIA' ? 'active' : ''}`}
          onClick={() => setAba('CONFERENCIA')}
        >
          Conferência
        </button>
        <button
          type="button"
          className={`recepcao-tab ${aba === 'COTACAO' ? 'active' : ''}`}
          onClick={() => setAba('COTACAO')}
        >
          Cotação online
        </button>
      </div>

      {aba === 'CONFERENCIA' && (
      <>
      {/* Banner de Rejeição */}
      {orcamento.estado === 'REJEITADO' && (
        <div className="cilia-rejection-banner">
          <div className="banner-content">
            <span className="banner-icon">!</span>
            <div className="banner-text">
              <strong>Orçamento Rejeitado:</strong> {motivoDaReprovacao || 'Nenhum motivo informado.'}
            </div>
          </div>
          {!somenteLeitura && !isVersaoSuperada && (
            <button
              className="cilia-btn danger-reopen"
              disabled={reabrirMutation.isPending}
              onClick={() => reabrirMutation.mutate()}
            >
              {reabrirMutation.isPending ? 'Reabrindo...' : 'Ajustar e Reenviar'}
            </button>
          )}
        </div>
      )}

      {/*
        Conferência concluída SEM requisição (13/08/2026). O banner nasceu para o caso "tudo
        atendido pelo estoque", que o ADR 0006 (20/08/2026) aposentou; ele continua valendo
        para a versão que não trouxe peça comprável, onde reabrir é o caminho de quem quer
        rever a triagem ou o vínculo.
      */}
      {orcamento.estado === 'ENVIADO' && requisicoes.length === 0 && (
        <div className="cilia-rejection-banner banner-neutro">
          <div className="banner-content">
            <span className="banner-icon">!</span>
            <div className="banner-text">
              <strong>Conferência concluída sem requisição de compra.</strong>{' '}
              Esta versão não trouxe peça nova a comprar — as que aparecem já foram pedidas em versões
              anteriores. Reabra apenas se precisar rever a triagem.
            </div>
          </div>
          {!somenteLeitura && !isVersaoSuperada && (
            <button
              className="cilia-btn danger-reopen"
              disabled={reabrirMutation.isPending}
              onClick={() => reabrirMutation.mutate()}
            >
              {reabrirMutation.isPending ? 'Reabrindo...' : 'Reabrir para ajustar'}
            </button>
          )}
        </div>
      )}

      {/* Versão superada: já existe complemento mais novo — leitura + atalho */}
      {isVersaoSuperada && (
        <div className="cilia-rejection-banner" style={{ background: 'var(--tema-fundo, #EFF6FF)', borderColor: 'var(--tema-borda-azul, #93C5FD)' }}>
          <div className="banner-content">
            <span className="banner-icon" style={{ color: '#1d4ed8' }}>!</span>
            <div className="banner-text">
              <strong>Este orçamento tem continuação:</strong> você está vendo uma versão anterior (somente
              leitura). A versão atual é o <strong>{rotuloUltimaVersao}</strong>.
            </div>
          </div>
          <button
            className="cilia-btn primary"
            onClick={() => navigate(ORCAMENTOS_CILIA_DETAIL(ultimaVersaoAtiva.id))}
          >
            Ir para a versão atual
          </button>
        </div>
      )}

      {/* Complemento além do limite: aguardando a decisão do Chefe de Oficina */}
      {orcamento.estado === 'AGUARDANDO_AUTORIZACAO' && (
        <div className="cilia-rejection-banner" style={{ background: 'var(--tema-fundo, #FFF7ED)', borderColor: '#FDBA74' }}>
          <div className="banner-content">
            <span className="banner-icon">!</span>
            <div className="banner-text">
              <strong>Aguardando autorização do Chefe de Oficina:</strong> este é o 4º (ou posterior) complemento
              deste orçamento — a conferência fica bloqueada até o Chefe aprovar. Os Chefes já foram notificados;
              você receberá um aviso com a decisão.
            </div>
          </div>
        </div>
      )}
      {orcamento.estado === 'AUTORIZACAO_NEGADA' && (
        <div className="cilia-rejection-banner">
          <div className="banner-content">
            <span className="banner-icon">!</span>
            <div className="banner-text">
              <strong>Complemento negado pelo Chefe de Oficina:</strong>{' '}
              {orcamento.autorizacaoNota || 'Nenhum motivo informado.'} O orçamento foi ignorado e o fluxo não
              continua.
            </div>
          </div>
        </div>
      )}

      {/* Alterações do complemento (diff calculado no import) — sinalização pura:
          quem decide o que fazer com removidos/reduzidos é o orçamentista. */}
      {orcamento.tipo === 'COMPLEMENTAR' && orcamento.diffResumo && (
        <div className="cilia-detail-header-card" style={{ width: '100%', boxSizing: 'border-box' }}>
          <div className="header-main-info">
            <h3 style={{ margin: '0 0 12px' }}>
              Alterações do complemento{' '}
              <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--tema-texto-2, #5E5E5E)' }}>
                {orcamento.diffResumo.contadores.novos} nova(s) · {orcamento.diffResumo.contadores.qtdAumentada}{' '}
                com quantidade maior · {orcamento.diffResumo.contadores.herdados} já tratada(s) ·{' '}
                {orcamento.diffResumo.contadores.removidos} removida(s)
              </span>
            </h3>
            {/*
              Peça que saiu daqui e continua numa cotação em andamento no Portal (13/08/2026
              — item 20 do backlog do Portal). Só aparece com o módulo de Cotações ligado; o
              componente também é quem avisa quando a verificação não pôde ser feita.
            */}
            <AvisoCotacaoAberta
              numeroCilia={orcamento.numeroCilia}
              versaoCilia={orcamento.versaoCilia}
              diffResumo={orcamento.diffResumo}
            />
            {orcamento.diffResumo.removidos.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <strong style={{ fontSize: 14 }}>Itens removidos neste complemento:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {orcamento.diffResumo.removidos.map((r) => (
                    <li key={r.codigoCilia} style={{ fontSize: 14, color: 'var(--tema-texto, #3f3f3f)' }}>
                      <span className="font-mono">{r.codigoCilia}</span> — {r.descricao} ({r.quantidade} un)
                      {['ENVIADO', 'RECEBIDO'].includes(r.situacaoCompra)
                        ? ` — ATENÇÃO: já solicitada no orç. ${r.origemNumero} (v${r.origemVersao}), mas removida do orçamento — avalie o que fazer com a peça`
                        : ` — estava "${situacaoLabels[r.situacaoCompra] ?? r.situacaoCompra}" na v${r.origemVersao}, sem compra encaminhada`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {orcamento.diffResumo.quantidadeReduzida.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <strong style={{ fontSize: 14 }}>Itens com quantidade reduzida:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {orcamento.diffResumo.quantidadeReduzida.map((r) => (
                    <li key={r.codigoCilia} style={{ fontSize: 14, color: 'var(--tema-texto, #3f3f3f)' }}>
                      <span className="font-mono">{r.codigoCilia}</span> — {r.descricao}: {r.qtdAnterior} →{' '}
                      {r.qtdNova} un (situação anterior: {situacaoLabels[r.situacaoCompra] ?? r.situacaoCompra}) — nenhuma compra é desfeita
                      automaticamente
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {orcamento.diffResumo.reprocessados.length > 0 && (
              <div>
                <strong style={{ fontSize: 14 }}>Itens reprocessados (nunca tiveram compra encaminhada):</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {orcamento.diffResumo.reprocessados.map((r) => (
                    <li key={r.codigoCilia} style={{ fontSize: 14, color: 'var(--tema-texto, #3f3f3f)' }}>
                      <span className="font-mono">{r.codigoCilia}</span> — {r.descricao} (estava "
                      {situacaoLabels[r.situacaoAnterior] ?? r.situacaoAnterior}" na v{r.origemVersao}) — entrou na conferência normalmente
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Listagem de itens agrupados */}
      <div className="cilia-detail-groups-container">
        {situacoesOrdem.map((situacao) => {
          const itemsInGroup = itensOrdenados.filter(i => i.situacaoCompra === situacao);
          if (itemsInGroup.length === 0) return null;

          const groupTotalPages = Math.max(1, Math.ceil(itemsInGroup.length / ITEMS_PER_PAGE));
          // Clamp: itens podem mudar de grupo (ex: forçar compra) e a página salva ficar além do fim
          const groupPage = Math.min(groupPages[situacao] ?? 1, groupTotalPages);
          const pagedItems = itemsInGroup.slice((groupPage - 1) * ITEMS_PER_PAGE, groupPage * ITEMS_PER_PAGE);

          return (
            <div key={situacao} className="cilia-detail-group-card">
              <div className="group-card-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {situacaoLabels[situacao]} ({itemsInGroup.length})
                  <button
                    className="help-circle-btn"
                    onClick={() => setIsSituacoesHelpOpen(true)}
                    title="O que significa cada situação?"
                    style={{ width: 22, height: 22, fontSize: 13, flexShrink: 0 }}
                  >
                    ?
                  </button>
                </h3>
                {situacao === 'FORA_DO_FLUXO' && (
                  <p className="group-card-hint">
                    Itens do orçamento que não geram compra de peça (serviços e reparos executados pela
                    própria oficina) — exibidos para manter o retrato completo do orçamento do Cilia.
                  </p>
                )}
                {/*
                  12/08/2026 (pedido do Victor): "Solicitada" com peças herdadas fazia parecer
                  que TODAS iriam para cotação por esta versão. A requisição desta versão leva
                  só o que ela trouxe de novo — e quando nada é novo, ela vai vazia (BUG-002).
                  A contagem abaixo é o indicador explícito que faltava.
                */}
                {situacao === 'ENVIADO' && (
                  <p className="group-card-hint">
                    {(() => {
                      // `itemsInGroup`: o grupo INTEIRO, não a página — a contagem é do total.
                      const desta = itemsInGroup.filter((i) => i.origemDiff !== 'HERDADO');
                      const herdadas = itemsInGroup.length - desta.length;
                      if (herdadas === 0) {
                        return `Peças incluídas na requisição desta versão (${desta.length}) — são estas que seguem para cotação.`;
                      }
                      if (desta.length === 0) {
                        return (
                          `Nenhuma peça foi pedida por esta versão: as ${herdadas} abaixo já haviam sido solicitadas em versões ` +
                          'anteriores e não são pedidas de novo. Ou seja, esta versão não gerou requisição nenhuma.'
                        );
                      }
                      return (
                        `${desta.length} peça(s) foram pedidas por esta versão e seguem para cotação; ` +
                        `as outras ${herdadas} já haviam sido solicitadas antes e não são pedidas de novo.`
                      );
                    })()}
                    {/*
                      20/08/2026 (achado do Victor nos testes da at-08): a contagem de "Solicitada"
                      pode CAIR de uma versão para a outra — v2 mostrava 5, v3 mostrou 3 —, e quem
                      lê conclui que dois pedidos desapareceram. Não desapareceram: as peças saíram
                      do ORÇAMENTO no complemento, e esta tabela é a foto do orçamento desta versão,
                      não o extrato do que já se pediu na placa.

                      A informação existia no painel "Alterações do complemento", acima, mas os dois
                      blocos não se referenciavam — a queda da contagem ficava sem explicação no
                      lugar onde ela é vista. O aviso só aparece quando há peça removida que JÁ
                      HAVIA SIDO SOLICITADA: é o único caso em que a contagem cai.
                    */}
                    {(() => {
                      const removidasJaPedidas = (orcamento.diffResumo?.removidos ?? []).filter(
                        (r) => r.situacaoCompra === 'ENVIADO' || r.situacaoCompra === 'RECEBIDO',
                      );
                      if (removidasJaPedidas.length === 0) return null;
                      return (
                        <>
                          {' '}
                          <strong>
                            Outra(s) {removidasJaPedidas.length} peça(s) já solicitada(s) saíram do orçamento
                            neste complemento e por isso não aparecem nesta lista
                          </strong>{' '}
                          — esta tabela é a foto do orçamento desta versão, não o histórico de tudo o que já
                          foi pedido para a placa. Veja "Itens removidos neste complemento" no topo da tela: o
                          pedido delas continua existindo e precisa de decisão sua.
                        </>
                      );
                    })()}
                  </p>
                )}
              </div>
              <div className="TabelaScroll" style={{ width: '100%' }}>
                <div
                  className="TabelaContent"
                  style={{
                    /*
                      QUATRO colunas (13/08/2026 — pedido do Victor). A tabela tinha 10, e
                      mesmo com "célula é uma linha só" a descrição disputava espaço com os
                      selos e cortava. O resto — quantidade, referência de preço, triagem,
                      produto do ERP e status, com as respectivas AÇÕES — vive no
                      `ItemDetalheModal`, aberto pelo clique na descrição.
                      Sem `minWidth`: com 4 colunas a tabela cabe na tela e não precisa mais
                      de rolagem horizontal.
                    */
                    ['--table-columns' as any]:
                      'minmax(240px, 2.4fr) minmax(110px, 0.8fr) minmax(130px, 0.8fr) minmax(120px, 0.8fr)',
                    width: '100%',
                  }}
                >
                  <div className="Tabela TabelaHeader">
                    <div className="Placa">
                      <div className="TTulo">
                        <div className="Opcoes">Descrição</div>
                        <div className="Opcoes">Código Cilia</div>
                        <div className="Opcoes">Origem</div>
                        <div className="Opcoes">Valor Unitário</div>
                      </div>
                    </div>
                  </div>

                  {pagedItems.map((item) => {
                    /*
                      As derivações que existiam aqui (referência de preço, herdado, código
                      em edição, pendência de esteira) foram para o `ItemDetalheModal` junto
                      com as células que as consumiam — 13/08/2026. A linha ficou com o que
                      se lê de relance; o resto é um clique na descrição.
                    */
                    return (
                      <div 
                        key={item.id} 
                        id={`item-${item.id}`}
                        className={`Tabela ${budgetIsItemPending(item, situacao) ? 'row-has-pending' : ''}`}
                      >
                        {/*
                          Descrição CLICÁVEL (13/08/2026): abre o detalhe da peça com tudo o
                          que saiu da tabela — quantidade, referência de preço, triagem,
                          vínculo com o ERP, status e as ações. Os selos que disputavam esta
                          célula ("obs.", "Pedida na vN", "Qtd. aumentada") viraram avisos no
                          topo do modal, onde há espaço para explicar em vez de abreviar.
                        */}
                        <div className="Item">
                          <div className="Data item-descricao-cell">
                            <button
                              type="button"
                              className="item-descricao-link"
                              title={`${item.descricaoCilia} — clique para ver os detalhes da peça`}
                              onClick={() => setItemDetalheId(item.id)}
                            >
                              {item.descricaoCilia}
                            </button>
                            {/*
                              O selo "Forçada" vivia aqui (18/08/2026) para marcar na lista a peça
                              cuja compra o orçamentista forçou contra a regra de estoque. Saiu em
                              20/08/2026 com a própria ação (ADR 0006): sem regra que retire peça da
                              compra, não existe exceção a sinalizar — toda peça do grupo "A Comprar"
                              é comprada pelo mesmo motivo.
                            */}
                          </div>
                        </div>
                        <div className="Item font-mono">
                          <div className="Data">{item.codigoCilia}</div>
                        </div>
                        <div className="Item">
                          <div
                            className="Data"
                            title={`A peça entrou no orçamento nesta versão do Cilia (v${item.versaoOrigem ?? orcamento.versaoCilia})`}
                          >
                            {rotuloOrigem(item)}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">{formatCurrency(item.preco)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {itemsInGroup.length > ITEMS_PER_PAGE && (
                <div className="PaginaO" style={{ borderTop: '1px solid var(--tema-linha, #EDEDED)', width: '100%', boxSizing: 'border-box' }}>
                  <div className="Frame1000003950">
                    <div
                      className="Anterior"
                      onClick={() => groupPage > 1 && setGroupPages((prev) => ({ ...prev, [situacao]: groupPage - 1 }))}
                      style={{
                        cursor: groupPage === 1 ? 'not-allowed' : 'pointer',
                        color: groupPage === 1 ? '#BEBEBE' : '#ea5d0e',
                        fontWeight: groupPage === 1 ? '400' : '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <img src={groupPage === 1 ? ArrowLeftGray : ArrowLeftOrange} alt="Anterior" />
                      Anterior
                    </div>
                  </div>

                  <div className="Group20">
                    <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: 'var(--tema-texto-2, #5E5E5E)' }}>
                      Página <strong>{groupPage}</strong> de {groupTotalPages}
                    </span>
                  </div>

                  <div className="Frame1000003949">
                    <div
                      className="PrXima"
                      onClick={() => groupPage < groupTotalPages && setGroupPages((prev) => ({ ...prev, [situacao]: groupPage + 1 }))}
                      style={{
                        cursor: groupPage === groupTotalPages ? 'not-allowed' : 'pointer',
                        color: groupPage === groupTotalPages ? '#BEBEBE' : '#ea5d0e',
                        fontWeight: groupPage === groupTotalPages ? '400' : '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      Próxima
                      <img src={groupPage === groupTotalPages ? ArrowRightGray : ArrowRightOrange} alt="Próxima" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/*
        CARD DA REQUISIÇÃO DE COMPRA (at-11) — o produto final do módulo.

        Até 18/09/2026 o card mostrava a SOLICITAÇÃO: um CSV para o comprador lançar à mão no ERP,
        com o desfecho digitado aqui depois. Hoje a requisição é criada por API, tem código no
        Nasajon e a situação é lida de lá — o card deixou de pedir digitação e passou a informar.

        ⚠️ Numa versão MISTA são DUAS requisições (normal + CAMINHO VIP), por isso o card itera:
        mostrar só a primeira esconderia metade das peças de quem acompanha.
      */}
      {requisicoes.length > 0 && (
        <div className="cilia-solicitacao-card">
          <div className="solicitacao-card-header">
            <h4>{requisicoes.length > 1 ? 'Requisições de Compra' : 'Requisição de Compra'}</h4>
            {/*
              O botão existe porque o job roda de 10 em 10 minutos: quem está com a tela aberta
              esperando a aprovação não deve precisar do ciclo. Some quando não há o que consultar
              (tudo resolvido, ou tudo simulado) — botão que nunca muda nada ensina a ignorá-lo.
            */}
            {temRequisicaoAberta && !somenteLeitura && (
              <button
                className="link-action"
                onClick={() => atualizarRequisicoesMutation.mutate()}
                disabled={atualizarRequisicoesMutation.isPending}
                title="Consulta no Nasajon se a requisição já foi aprovada ou reprovada"
              >
                {atualizarRequisicoesMutation.isPending ? 'Consultando...' : 'Atualizar situação'}
              </button>
            )}
          </div>
          <div className="solicitacao-card-body">
            {requisicoes.map((req) => (
              <div key={`${req.versao}-${req.vip ? 'vip' : 'normal'}`} className="requisicao-linha">
                <div className="solicitacao-status-row">
                  {/*
                    🔴 O CÓDIGO DO ERP É O DADO PRINCIPAL: é por ele que o comprador acha o
                    documento no Nasajon. Requisição SIMULADA não tem código — e dizer isso é
                    obrigatório, senão um envio de desenvolvimento passa por envio de verdade.
                  */}
                  {req.nasajonCodigo ? (
                    <span className="requisicao-codigo" title="Código da requisição no Nasajon">
                      {req.nasajonCodigo}
                    </span>
                  ) : (
                    <span className="cilia-badge badge-cinza" title="Nada foi criado no Nasajon">
                      Simulada
                    </span>
                  )}

                  {/*
                    at-14: outra requisição com o MESMO número no Nasajon (Compras criou no mesmo
                    segundo). O acompanhamento não se confunde (é pelo id), mas Compras precisa saber.
                  */}
                  {req.codigoRepetido && (
                    <span
                      className="cilia-badge badge-vermelho"
                      title="O Nasajon tem outra requisição com este mesmo número. Avise Compras."
                    >
                      Número repetido no Nasajon
                    </span>
                  )}

                  {/* O marcador que diz ao comprador para NÃO comprar estas peças. */}
                  {req.vip && (
                    <span
                      className="cilia-badge badge-laranja"
                      title="Peças já compradas na rua — o Motivo da Requisição avisa o comprador para não comprar"
                    >
                      CAMINHO VIP
                    </span>
                  )}

                  <span className="status-label">Situação:</span>
                  <span className={`solicitacao-status status-${req.status.toLowerCase()}`}>
                    {rotuloDesfecho(req.status)}
                  </span>

                  {/*
                    ⚠️ Situação sem data de conferência não distingue "está assim agora" de
                    "ninguém pergunta desde ontem" — e é justamente essa dúvida que faria alguém
                    ligar para Compras à toa.
                  */}
                  {req.ultimaVerificacaoEm && (
                    <span className="solicitacao-desfecho-data">
                      conferido em {new Date(req.ultimaVerificacaoEm).toLocaleString('pt-BR')}
                    </span>
                  )}
                </div>

                {/* O motivo quando há: reprovação sem explicação manda o orçamentista adivinhar. */}
                {req.motivoRejeicao && (
                  <p className="solicitacao-desfecho-hint">{req.motivoRejeicao}</p>
                )}

                {/*
                  O vocabulário do ERP ao lado do nosso: quem for conferir na tela do Nasajon
                  procura por "APROVADO"/"pendente", não pelos nomes daqui.
                */}
                {(req.estadoWorkflowErp || req.situacaoErp) && (
                  <p className="historico-orc-meta">
                    No Nasajon: aprovação <strong>{req.estadoWorkflowErp ?? 'não iniciada'}</strong>
                    {req.situacaoErp ? <> · compra <strong>{req.situacaoErp}</strong></> : null}
                  </p>
                )}
              </div>
            ))}

            {/*
              ⚠️ A requisição APROVADA continua com a compra "pendente" no ERP até virar pedido —
              e quem lê os dois vocabulários lado a lado conclui que algo travou. Dizer isso uma
              vez, no card, evita a ligação para Compras.
            */}
            {requisicoes.some((r) => r.status === 'APROVADA') && (
              <p className="solicitacao-disclaimer">
                A situação de compra continua "pendente" no Nasajon mesmo depois de aprovada: ela só
                muda quando Compras transforma a requisição em pedido.
              </p>
            )}

            {/* Contexto na versão superada: a requisição acima é da versão
                antiga — o orçamento em si continua em outra versão. */}
            {isVersaoSuperada && ultimaVersaoAtiva && (
              <p className="historico-orc-meta" style={{ marginTop: 8 }}>
                {requisicoes.length > 1 ? 'Estas requisições pertencem' : 'Esta requisição pertence'} a{' '}
                <strong>esta versão</strong> do orçamento
                {orcamento.tipo === 'INICIAL' ? ' (Inicial)' : ''} — o orçamento segue no{' '}
                <strong>{rotuloUltimaVersao}</strong>, atualmente{' '}
                {rotuloEstadoVersao(ultimaVersaoAtiva)}.
              </p>
            )}
          </div>
        </div>
      )}

      </>
      )}

      {/* Cotação online (at-15): menor preço por canal, pesquisado sozinho na conclusão. */}
      {aba === 'COTACAO' && <CotacaoOnlineSecao orcamentoId={budgetId} placa={orcamento.placa} />}

      {/* Rodapé Fixo — só na aba de conferência, que é onde se conclui */}
      {aba === 'CONFERENCIA' && !somenteLeitura && (orcamento.estado === 'EM_CONFERENCIA' || orcamento.estado === 'EM_AJUSTE') && (
        <div className="cilia-detail-footer-bar">
          <div className="footer-counters">
            {totalPendencies > 0 ? (
              <span className="pendencies-alert">
                <strong>{totalPendencies} pendências:</strong> {noEsteiraCount} sem esteira · {naoVerificadoCount} não verificado(s) no ERP
              </span>
            ) : (
              <span className="pendencies-ok">
                Tudo correto! Nenhuma pendência encontrada.
              </span>
            )}
            {naoVerificadoCount > 0 && (
              <button
                className="cilia-btn secondary"
                disabled={reverificarMutation.isPending}
                onClick={() => reverificarMutation.mutate()}
                title="Refaz o reconhecimento das peças que ficaram pendentes porque o Nasajon estava fora do ar na importação"
              >
                {reverificarMutation.isPending ? 'Reverificando...' : 'Reverificar no Nasajon'}
              </button>
            )}
          </div>
          <button 
            className="cilia-btn primary conclude-btn"
            disabled={totalPendencies > 0 || concluirMutation.isPending}
            onClick={() => {
              setEscolhaDestino({
                tipoOficina: orcamento?.tipoOficinaSugerido ?? null,
                tipoVeiculo: orcamento?.tipoVeiculoSugerido ?? null,
              });
              setIsConcludeConfirmOpen(true);
            }}
          >
            {concluirMutation.isPending ? 'Concluindo...' : 'Concluir e Enviar'}
          </button>
        </div>
      )}

      {/* Modal de Confirmação de Conclusão */}
      {isConcludeConfirmOpen && (
        <div className="detail-modal-overlay">
          <div className="detail-modal-box">
            <div className="detail-modal-header">
              <h3>Confirmar Conclusão da Conferência</h3>
            </div>
            <div className="detail-modal-body">
              {/*
                PEÇA SEM VÍNCULO VIRA PRODUTO NOVO NO ERP (regra de 28/07/2026), e isso tem de ser dito
                ANTES de confirmar, com os nomes (pedido do Victor, 13/10/2026): vincular não é
                obrigatório, então o silêncio vale como "criar como nova" — e uma peça que já existe no
                Nasajon com outro código vira duplicata no cadastro de lá, sem erro em lugar nenhum.
              */}
              {pecasSemVinculo.length > 0 && (
                <div className="aviso-de-tela" role="alert">
                  <strong className="aviso-de-tela-titulo">
                    Atenção: {pecasSemVinculo.length === 1
                      ? '1 peça não foi vinculada a um produto do Nasajon'
                      : `${pecasSemVinculo.length} peças não foram vinculadas a um produto do Nasajon`}
                  </strong>
                  <p>
                    {pecasSemVinculo.length === 1 ? 'Ela será cadastrada' : 'Elas serão cadastradas'} no Nasajon como{' '}
                    {pecasSemVinculo.length === 1 ? 'produto novo' : 'produtos novos'}, com código e descrição do Cilia.
                    Se já existir no ERP com outro código, o cadastro fica duplicado. Para vincular, cancele, abra a
                    peça e use o campo <strong>Produto no Nasajon</strong>.
                  </p>
                  <ul>
                    {pecasSemVinculo.map((i) => (
                      <li key={i.id}>
                        <span className="font-mono">{i.codigoCilia}</span> — {i.descricaoCilia}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p>Ao concluir a conferência deste orçamento:</p>
              <ul>
                <li>Todas as peças do grupo <strong>"A Comprar"</strong> entram na requisição de compra, na quantidade que o orçamento pede.</li>
                {pecasSemVinculo.length === 0 && (
                  <li>Todas as peças a comprar estão vinculadas a produtos já existentes no Nasajon — nenhum produto novo será criado.</li>
                )}
                <li>A requisição é criada <strong>no Nasajon</strong>, em seu nome, e segue para a aprovação de lá.</li>
                {orcamento.teste && (
                  <li>
                    Esta é uma <strong>requisição de teste</strong>: o Motivo no Nasajon começa por{' '}
                    <strong>"TESTE - NÃO COMPRAR"</strong>, e o comprador não compra as peças.
                  </li>
                )}
                {/*
                  ⚠️ A versão mista gera DUAS requisições, e quem confirma precisa saber disso ANTES:
                  depois, são dois documentos a acompanhar no ERP. O aviso só aparece quando é o caso
                  — dito sempre, viraria ruído em toda conclusão.
                */}
                {(orcamento.itens ?? []).some((i) => i.vip && i.situacaoCompra === 'A_COMPRAR') && (
                  <li>
                    Há peças marcadas como <strong>já compradas na rua (CAMINHO VIP)</strong>: elas vão numa{' '}
                    <strong>requisição separada</strong>, avisando o comprador para não comprá-las. Serão{' '}
                    <strong>duas requisições</strong> a acompanhar no Nasajon.
                  </li>
                )}
              </ul>
              <EscolhaDoDestino
                valor={escolhaDestino}
                onChange={setEscolhaDestino}
                sugestao={{
                  tipoOficina: orcamento?.tipoOficinaSugerido ?? null,
                  tipoVeiculo: orcamento?.tipoVeiculoSugerido ?? null,
                }}
                desabilitado={concluirMutation.isPending}
              />
              <p className="simulation-note"><em>Nota: Nenhuma gravação real é executada em caso de modo simulação ativo no servidor.</em></p>
              {/*
                A conclusão faz várias idas ao Nasajon em sequência (unidades, produtos, cadastro das peças
                novas, a requisição) e leva segundos. Sem este estado o botão só apagava e a pessoa não
                sabia se o clique valeu (medido pelo Victor, 13/10/2026).
              */}
              {concluirMutation.isPending && (
                <p className="conclude-andamento" role="status">
                  <span className="cilia-spinner" aria-hidden="true" />
                  <span>
                    Concluindo: conferindo as peças no cadastro do Nasajon e criando a requisição. Pode levar alguns
                    segundos — não feche esta janela.
                  </span>
                </p>
              )}
            </div>
            <div className="detail-modal-footer">
              <button
                className="cilia-btn cancel"
                onClick={() => setIsConcludeConfirmOpen(false)}
                disabled={concluirMutation.isPending}
              >
                Cancelar
              </button>
              <button
                className="cilia-btn confirm"
                onClick={() => concluirMutation.mutate()}
                disabled={
                  concluirMutation.isPending || !escolhaDestino.tipoOficina || !escolhaDestino.tipoVeiculo
                }
                title={
                  !escolhaDestino.tipoOficina || !escolhaDestino.tipoVeiculo
                    ? 'Escolha onde o veículo está e o tipo de veículo'
                    : undefined
                }
              >
                {concluirMutation.isPending ? (
                  <>
                    <span className="cilia-spinner cilia-spinner--claro" aria-hidden="true" /> Concluindo...
                  </>
                ) : (
                  'Concluir Conferência'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
       * ⚠️ OS DOIS MODAIS DE DESFECHO MANUAL SAÍRAM NA at-11 (18/09/2026).
       *
       * Eram "Confirmar Aprovação da Solicitação" e "Registrar Rejeição da Solicitação": alguém
       * olhava a tela do Nasajon e transcrevia o desfecho aqui, com o motivo digitado à mão. Com a
       * requisição criada por API o desfecho é LIDO do ERP, e manter a digitação permitiria marcar
       * "aprovada" no módulo com a requisição reprovada lá.
       */}
      {/* Modal Histórico do PMC (clique no valor) */}
      {/*
        Detalhe da peça (13/08/2026): tudo o que saiu da tabela de 10 colunas, com espaço
        para os controles respirarem. Montagem condicional — o estado inicial do modal é
        recalculado a cada abertura (mesmo princípio dos modais de filtro da Oficina).
      */}
      {itemDetalhe && (
        <ItemDetalheModal
          item={itemDetalhe}
          situacao={situacaoDoItemDetalhe}
          isEditable={isEditable}
          esteiras={filteredEsteiras}
          referencia={referenciaPreco(itemDetalhe)}
          margemPmcPct={margemPmcPct}
          mesesJanela={mesesJanela}
          salvando={updateItemMutation.isPending || reconhecerMutation.isPending}
          vinculandoProduto={reconhecerMutation.isPending}
          unidades={unidadesQuery.data ?? []}
          unidadesCarregando={unidadesQuery.isLoading}
          unidadesErro={unidadesQuery.isError}
          onFechar={() => setItemDetalheId(null)}
          onEsteiraChange={handleEsteiraChange}
          onUnidadeChange={(itemId, unidade) => updateItemMutation.mutate({ itemId, data: { unidade } })}
          onVipChange={(itemId, vip) => updateItemMutation.mutate({ itemId, data: { vip } })}
          onVincularProduto={(itemId, produto) =>
            reconhecerMutation.mutate({ itemId, produtoNasajonId: produto.nasajonId, codigoNasajon: produto.codigo })
          }
          onDesfazerVinculo={(itemId) => reconhecerMutation.mutate({ itemId, produtoNasajonId: null })}
          onVerHistoricoPreco={(it) => { setHistoricoTarget(it); setHistoricoPage(1); }}
        />
      )}

      {historicoTarget && (
        <div className="detail-modal-overlay" onClick={() => setHistoricoTarget(null)}>
          <div className="detail-modal-box historico-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h3>Referência de preço no Nasajon</h3>
            </div>
            <div className="detail-modal-body">
              <p className="reconhecer-item-info">
                <strong>{historicoTarget.descricaoCilia}</strong>
                <span className="font-mono"> · Cód. Cilia {historicoTarget.codigoCilia}</span>
                {historicoTarget.codigoNasajon && (
                  <span className="font-mono"> · Produto ERP {historicoTarget.codigoNasajon}</span>
                )}
              </p>

              {/* Explicação do valor em VERMELHO na conferência (alerta de preço) */}
              {(() => {
                const refHistorico = referenciaPreco(historicoTarget);
                const alertaHistorico =
                  refHistorico !== null &&
                  parseFloat(historicoTarget.preco) > refHistorico * (1 + margemPmcPct / 100);
                return alertaHistorico ? (
                  <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>
                    O valor desta peça aparece <strong>em vermelho</strong> na conferência porque o preço do
                    orçamento ({formatCurrency(historicoTarget.preco)}) está mais de {margemPmcPct}% acima da
                    média de compras ({formatCurrency(refHistorico)}) — confira o preço antes de comprar. A
                    margem é configurável em Administração do Sistema &rarr; Requisições &rarr; "Alerta de preço".
                  </p>
                ) : null;
              })()}

              {buscandoHistorico && <p className="value-read-only">Consultando o Nasajon...</p>}

              {!buscandoHistorico && !!erroHistorico && (
                <p className="value-read-only">
                  {(erroHistorico as any)?.message || 'Não foi possível consultar o Nasajon agora.'}
                </p>
              )}

              {!buscandoHistorico && !erroHistorico && historico && (
                <>
                  <div className="historico-resumo">
                    <div className="historico-resumo-item">
                      <span className="label">Valor no orçamento (Cilia)</span>
                      <span className="value">{formatCurrency(historicoTarget.preco)}</span>
                    </div>
                    <div className="historico-resumo-item destaque">
                      <span className="label">Média de compras ({mesesJanela} meses)</span>
                      <span className="value">
                        {historicoTarget.mediaCompras && parseFloat(historicoTarget.mediaCompras) > 0
                          ? `${formatCurrency(historicoTarget.mediaCompras)} · ${historicoTarget.mediaComprasQtd} compra(s)`
                          : 'sem compras no período'}
                      </span>
                    </div>
                    {/* "Saldo em estoque" saiu deste resumo em 20/08/2026 (ADR 0006): o painel é
                        de referência de PREÇO, e o saldo ali sugeria participação na decisão de
                        compra. O backend deixou de devolver o campo. */}
                    <div className="historico-resumo-item">
                      <span className="label">Última compra</span>
                      <span className="value">
                        {historico.ultimaCompra
                          ? `${formatCurrency(historico.ultimaCompra.valor)} · ${formatDateOnly(historico.ultimaCompra.data)}`
                          : '—'}
                      </span>
                    </div>
                  </div>

                  <h4 className="historico-titulo">Movimentações desta peça no ERP</h4>

                  {!historico.extrato.disponivel ? (
                    <p className="solicitacao-disclaimer">
                      Não foi possível consultar o extrato no Nasajon agora — o custo médio acima segue válido.
                    </p>
                  ) : historico.extrato.movimentacoes.length === 0 ? (
                    <p className="value-read-only">Nenhuma movimentação registrada para esta peça.</p>
                  ) : (
                    (() => {
                      const movs = historico.extrato.movimentacoes;
                      const totalPaginas = Math.max(1, Math.ceil(movs.length / HISTORICO_POR_PAGINA));
                      const pagina = Math.min(historicoPage, totalPaginas);
                      const visiveis = movs.slice(
                        (pagina - 1) * HISTORICO_POR_PAGINA,
                        pagina * HISTORICO_POR_PAGINA,
                      );
                      return (
                        <>
                          <div className="historico-tabela">
                            <div className="historico-linha historico-header">
                              <span>Data</span>
                              <span>Operação</span>
                              <span>Qtde</span>
                              <span>Valor unit.</span>
                              <span>Documento</span>
                            </div>
                            {visiveis.map((m) => {
                              const ehCompra = (m.operacao_codigo ?? '').toUpperCase() === 'COMPRA';
                              return (
                                <div key={m.id} className={`historico-linha ${ehCompra ? 'linha-compra' : ''}`}>
                                  <span>{formatDateOnly(m.data)}</span>
                                  <span title={m.historico ?? undefined}>
                                    {ehCompra ? 'Compra' : `${m.sinal === 'saida' ? '↓' : '↑'} ${m.operacao_codigo || '—'}`}
                                  </span>
                                  <span>{m.quantidade ? parseFloat(m.quantidade) : '—'}</span>
                                  <span>{formatCurrency(m.valor)}</span>
                                  <span className="font-mono">{m.numero || '—'}</span>
                                </div>
                              );
                            })}
                          </div>
                          <p className="historico-nota">
                            Ajustes de inventário aparecem junto das compras — só as linhas "Compra"
                            (destacadas) são entradas por nota de compra.
                          </p>
                          {totalPaginas > 1 && (
                            <div className="historico-paginacao">
                              <button
                                className="cilia-btn secondary"
                                disabled={pagina === 1}
                                onClick={() => setHistoricoPage(pagina - 1)}
                              >
                                Anterior
                              </button>
                              <span className="value-read-only">
                                Página {pagina} de {totalPaginas} · {movs.length} movimentações
                              </span>
                              <button
                                className="cilia-btn secondary"
                                disabled={pagina === totalPaginas}
                                onClick={() => setHistoricoPage(pagina + 1)}
                              >
                                Próxima
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()
                  )}
                </>
              )}
            </div>
            <div className="detail-modal-footer">
              <button className="cilia-btn secondary" onClick={() => setHistoricoTarget(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* O modal "Reconhecer peça" existia aqui até 13/10/2026: a busca no cadastro do Nasajon
          passou a viver dentro do detalhe da peça (`BuscaProdutoNasajon`), no próprio campo. */}

      {/* O modal "Forçar compra" existia aqui até 20/08/2026 (ADR 0006): pedia justificativa
          para comprar peça que o estoque do Nasajon havia retirado do funil. Sem a regra, não
          há decisão do sistema de que discordar. */}

      {/* Drawer Como Funciona */}
      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Conferência de Orçamento"
        secoes={helpSections}
      />

      <ComoFunciona
        isOpen={isSituacoesHelpOpen}
        onClose={() => setIsSituacoesHelpOpen(false)}
        titulo="Situações das peças"
        secoes={situacaoAjudaSections}
      />

      {/* Histórico do Orçamento — modal central com TODAS as peças, bloco por
          versão (visão completa sem sair da tela; link para cada versão). */}
      {isHistoricoOpen && (
        <div className="detail-modal-overlay" onClick={() => setIsHistoricoOpen(false)}>
          <div
            className="detail-modal-box"
            style={{ width: 'min(980px, 96vw)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="detail-modal-header">
              <h3>Histórico do Orçamento {orcamento.numeroCilia} — {orcamento.placa}</h3>
            </div>
            <div className="detail-modal-body">
              {buscandoHistoricoOrc && <p className="value-read-only">Carregando o histórico...</p>}
              {!buscandoHistoricoOrc && !historicoOrc && (
                <p className="value-read-only">Não foi possível carregar o histórico agora.</p>
              )}
              {(historicoOrc?.versoes ?? []).map((v) => {
                const ordinal = v.tipo === 'COMPLEMENTAR' ? ordinalComplemento(v.versaoCilia) : null;
                const titulo =
                  v.tipo === 'INICIAL'
                    ? `Inicial (v${v.versaoCilia})`
                    : v.estado === 'AUTORIZACAO_NEGADA'
                      ? `Complemento negado (v${v.versaoCilia})`
                      : `${ordinal ?? '—'}º Complemento (v${v.versaoCilia})`;
                return (
                  <div key={v.id} className="historico-orc-bloco">
                    <div className="historico-orc-cabecalho">
                      <div>
                        <strong>{titulo}</strong>
                        <span className={`cilia-badge ${
                          v.estado === 'APROVADO' ? 'badge-verde' :
                          ['REJEITADO', 'AUTORIZACAO_NEGADA'].includes(v.estado) ? 'badge-vermelho' :
                          ['EM_AJUSTE', 'AGUARDANDO_AUTORIZACAO'].includes(v.estado) ? 'badge-laranja' :
                          v.estado === 'EM_CONFERENCIA' ? 'badge-azul' : 'badge-cinza'
                        }`} style={{ marginLeft: 8 }}>
                          {rotuloEstadoVersao(v)}
                        </span>
                      </div>
                      <div className="historico-orc-meta">
                        Importado em {dataHoraHistorico(v.createdAt)} por {v.importadoPorNome || '—'}
                        {v.conferidoEm
                          ? ` · concluído em ${dataHoraHistorico(v.conferidoEm)} por ${v.conferidoPorNome || '—'}`
                          : ''}
                        {v.id !== orcamento.id ? (
                          <button
                            className="link-action"
                            style={{ marginLeft: 8 }}
                            onClick={() => {
                              setIsHistoricoOpen(false);
                              navigate(ORCAMENTOS_CILIA_DETAIL(v.id));
                            }}
                          >
                            Abrir esta versão
                          </button>
                        ) : (
                          <span style={{ marginLeft: 8, fontWeight: 600 }}>(você está aqui)</span>
                        )}
                      </div>
                    </div>

                    <div className="historico-orc-tabela">
                      <div className="historico-orc-linha historico-orc-header">
                        <span>Código</span>
                        <span>Descrição</span>
                        <span>Qtd</span>
                        <span>Valor unit.</span>
                        <span>Esteira</span>
                        <span>Situação</span>
                      </div>
                      {v.itens.map((item, idx) => {
                        const papel = papelDaPeca(item, v);
                        return (
                          <div key={`${item.codigoCilia}-${idx}`} className="historico-orc-linha">
                            <span className="font-mono">{item.codigoCilia}</span>
                            <span>
                              {item.descricaoCilia}
                              {papel && (
                                <span className={`cilia-badge ${papel.classe}`} style={{ marginLeft: 6 }}>
                                  {papel.rotulo}
                                </span>
                              )}
                            </span>
                            <span>{parseFloat(item.quantidade)}</span>
                            <span>{formatCurrency(item.preco)}</span>
                            <span>{item.esteiraNome || '—'}</span>
                            <span>{situacaoLabels[item.situacaoCompra] ?? item.situacaoCompra}</span>
                          </div>
                        );
                      })}
                      {(v.diffResumo?.removidos ?? []).map((r) => (
                        <div key={`rem-${r.codigoCilia}`} className="historico-orc-linha historico-orc-removida">
                          <span className="font-mono">{r.codigoCilia}</span>
                          <span>
                            {r.descricao}
                            <span className="cilia-badge badge-vermelho" style={{ marginLeft: 6 }}>
                              Removida nesta versão
                              {['ENVIADO', 'RECEBIDO'].includes(r.situacaoCompra) ? ' (já havia sido solicitada)' : ''}
                            </span>
                          </span>
                          <span>{r.quantidade}</span>
                          <span>—</span>
                          <span>—</span>
                          <span>{situacaoLabels[r.situacaoCompra] ?? r.situacaoCompra}</span>
                        </div>
                      ))}
                    </div>

                    {(v.requisicoes ?? []).length > 0 && (
                      <div className="historico-orc-meta">
                        {(v.requisicoes ?? []).map((r) => (
                          // A chave inclui o `vip`: numa versão mista são duas requisições da
                          // MESMA versão, e só o número as tornaria duplicadas para o React.
                          <div key={`${r.versao}-${r.vip ? 'vip' : 'normal'}`}>
                            {/* O código do ERP quando existe; requisição simulada não tem. */}
                            {r.nasajonCodigo ?? `Requisição v${r.versao}`}
                            {r.vip ? ' (CAMINHO VIP)' : ''} — {rotuloDesfecho(r.status).toLowerCase()}
                            {r.motivoRejeicao ? ` · motivo: ${r.motivoRejeicao}` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {v.autorizadoEm && (
                      <div className="historico-orc-meta">
                        {v.estado === 'AUTORIZACAO_NEGADA' ? 'Negado' : 'Autorizado'} pelo Chefe de Oficina em{' '}
                        {dataHoraHistorico(v.autorizadoEm)} por {v.autorizadoPorNome || '—'}
                        {v.autorizacaoNota ? ` · nota: ${v.autorizacaoNota}` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="detail-modal-footer">
              <button className="cilia-btn secondary" onClick={() => setIsHistoricoOpen(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportXmlModal
        isOpen={isComplementoModalOpen}
        onClose={() => {
          setIsComplementoModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['orcamento-cilia'] });
        }}
        restricaoComplemento={{ numeroCilia: orcamento.numeroCilia, placa: orcamento.placa }}
      />
    </div>
  );
};

// Destaque de linha pendente — mesma regra da trava do backend: só itens que
// serão comprados (A_COMPRAR) precisam de triagem/verificação.
const budgetIsItemPending = (item: any, situacao: string): boolean => {
  if (situacao !== 'A_COMPRAR') return false;
  return !item.esteiraIdSoac || item.produtoStatus === 'NAO_VERIFICADO';
};

export default OrcamentoCiliaDetail;
